/**
 * Notificações do sistema no app Android (Capacitor Local Notifications).
 *
 * Por que este módulo existe: a Notification API do navegador não funciona no
 * WebView do Capacitor. O `useNotifications` detectava isso e desligava o
 * recurso inteiro (`permissionStatus: 'unsupported'`), e como `canSendNotification`
 * exige permissão concedida, no app NADA passava — nem o aviso do sistema, nem
 * a entrada no centro de notificações dentro do jogo. A tela de configurações
 * prometia "receba notificações mesmo com o jogo fechado" e o botão "Ativar"
 * não fazia nada.
 *
 * Aqui a mesma promessa é cumprida pelo caminho nativo: pedido de permissão de
 * verdade (POST_NOTIFICATIONS no Android 13+), avisos imediatos e agendamentos
 * entregues ao Android — que os dispara mesmo com o app fechado, coisa que o
 * `setInterval` do lado web não consegue fazer.
 *
 * No web todas as funções são no-op e retornam "indisponível"; quem chama
 * continua usando o caminho do navegador.
 */
import { Capacitor } from "@capacitor/core";

export type NotificationPermission = "default" | "granted" | "denied" | "unsupported";

type LocalNotificationsPlugin = typeof import("@capacitor/local-notifications").LocalNotifications;

let _plugin: LocalNotificationsPlugin | null = null;
let _loading: Promise<LocalNotificationsPlugin | null> | null = null;

/** O app roda no nativo e o plugin foi empacotado neste build? */
export function isNativeNotificationsSupported(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("LocalNotifications");
  } catch {
    return false;
  }
}

async function loadPlugin(): Promise<LocalNotificationsPlugin | null> {
  if (_plugin) return _plugin;
  if (!isNativeNotificationsSupported()) return null;
  if (!_loading) {
    _loading = import("@capacitor/local-notifications")
      .then((mod) => {
        _plugin = mod.LocalNotifications;
        return _plugin;
      })
      .catch((error) => {
        console.warn("[Notifications] Plugin nativo indisponível:", error);
        return null;
      });
  }
  return _loading;
}

/**
 * IDs do Android são inteiros de 32 bits; os do store são strings
 * (`notif_<timestamp>_<random>`). Hash estável para que cancelar depois
 * atinja exatamente o agendamento criado.
 */
export function toNativeId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  // Positivo e dentro do range que o Android aceita
  return Math.abs(hash) % 2_147_483_000 || 1;
}

export async function getNativePermission(): Promise<NotificationPermission> {
  const plugin = await loadPlugin();
  if (!plugin) return "unsupported";
  try {
    const { display } = await plugin.checkPermissions();
    if (display === "granted") return "granted";
    if (display === "denied") return "denied";
    return "default";
  } catch {
    return "unsupported";
  }
}

export async function requestNativePermission(): Promise<NotificationPermission> {
  const plugin = await loadPlugin();
  if (!plugin) return "unsupported";
  try {
    const { display } = await plugin.requestPermissions();
    if (display === "granted") return "granted";
    if (display === "denied") return "denied";
    return "default";
  } catch {
    return "denied";
  }
}

/** Aviso imediato do sistema. */
export async function showNativeNotification(
  title: string,
  options?: { body?: string; tag?: string; data?: unknown },
): Promise<boolean> {
  const plugin = await loadPlugin();
  if (!plugin) return false;
  try {
    await plugin.schedule({
      notifications: [
        {
          id: toNativeId(`${options?.tag ?? "now"}_${Date.now()}`),
          title,
          body: options?.body ?? "",
          extra: (options?.data as Record<string, unknown>) ?? undefined,
        },
      ],
    });
    return true;
  } catch (error) {
    console.warn("[Notifications] Falha ao exibir aviso nativo:", error);
    return false;
  }
}

/**
 * Entrega o agendamento ao Android. Diferente do caminho web (que depende do
 * jogo estar aberto para o `setInterval` disparar), isto acorda com o app
 * fechado — que é justamente o que a tela de configurações promete.
 */
export async function scheduleNativeNotification(params: {
  id: string;
  title: string;
  body: string;
  at: number;
  data?: Record<string, unknown>;
}): Promise<boolean> {
  const plugin = await loadPlugin();
  if (!plugin) return false;
  // Horário já passado: o Android dispararia na hora; melhor ignorar.
  if (params.at <= Date.now()) return false;
  try {
    await plugin.schedule({
      notifications: [
        {
          id: toNativeId(params.id),
          title: params.title,
          body: params.body,
          schedule: { at: new Date(params.at), allowWhileIdle: true },
          extra: params.data,
        },
      ],
    });
    return true;
  } catch (error) {
    console.warn("[Notifications] Falha ao agendar aviso nativo:", error);
    return false;
  }
}

export async function cancelNativeNotifications(ids: string[]): Promise<void> {
  const plugin = await loadPlugin();
  if (!plugin || ids.length === 0) return;
  try {
    await plugin.cancel({ notifications: ids.map((id) => ({ id: toNativeId(id) })) });
  } catch {
    // Agendamento pode já ter disparado — nada a cancelar
  }
}
